import { useEffect, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { PersonAdd, CheckCircle, People, HourglassEmpty, Block } from '@mui/icons-material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { profileApi } from '@api/endpoints/profile'
import { socialApi } from '@api/endpoints/social'
import { useAuthStore } from '@store/authStore'
import { UserFacingFriendStatus, type FriendDto } from '@api/types/social'

interface FriendRequestScanDialogProps {
  /** Target userId extracted from the scanned QR payload. */
  targetUserId: string | null
  onClose: () => void
}

/**
 * Modal that fires when someone scans a friend-share QR token. Loads the
 * target user's public profile + the current user's friends list, then
 * either reports the existing relationship status or offers a "Send Request"
 * button wired to POST /api/me/friends.
 *
 * Edge cases handled inline:
 *  - Scanning your own QR
 *  - Unauthenticated
 *  - Already connected / pending in either direction / blocked
 *  - Unknown server-side rejection (surfaces the API message)
 *
 * Renders nothing when targetUserId is null (caller controls open state).
 */
export default function FriendRequestScanDialog({ targetUserId, onClose }: FriendRequestScanDialogProps) {
  const queryClient = useQueryClient()
  const currentUser = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isSelf = !!(currentUser?.userId && targetUserId && currentUser.userId === targetUserId)
  const shouldFetch = !!(targetUserId && isAuthenticated && !isSelf)

  // Profile preview for the target user.
  const {
    data: profile,
    isLoading: profileLoading,
    error: profileError,
  } = useQuery({
    queryKey: ['user-profile', targetUserId],
    queryFn: () => profileApi.getUserProfile(targetUserId!),
    enabled: shouldFetch,
    staleTime: 60_000,
  })

  // Friends list — shared cache key with BuddiesPage / PendingRequestsList
  // so the dialog reflects any locally-cached state and refreshes the same
  // entries other views are watching.
  const { data: friends, isLoading: friendsLoading } = useQuery({
    queryKey: ['friends'],
    queryFn: socialApi.getFriends,
    enabled: shouldFetch,
  })

  const existing: FriendDto | undefined = friends?.find((f) => f.userId === targetUserId)

  const [submitting, setSubmitting] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  // Reset transient send state whenever the target changes (new scan).
  useEffect(() => {
    setSubmitting(false)
    setSendError(null)
    setSent(false)
  }, [targetUserId])

  if (!targetUserId) return null

  const loading = shouldFetch && (profileLoading || friendsLoading)

  const handleSend = async () => {
    setSubmitting(true)
    setSendError(null)
    try {
      await socialApi.sendFriendRequest({ friendId: targetUserId })
      setSent(true)
      // Refresh shared friends cache so other views (and a re-scan) see the
      // new pending row immediately.
      queryClient.invalidateQueries({ queryKey: ['friends'] })
    } catch (e: any) {
      const data = e?.response?.data
      const message = data?.message || data?.Message || e?.message || 'Could not send buddy request.'
      setSendError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const avatarSrc = profile?.avatarUrl || profile?.avatar
  const displayName = profile?.displayName ?? existing?.displayName ?? 'this user'

  // Decide which state to render based on the existing relationship, if any.
  // Default ('canSend') is the path where no friendship row exists — or it
  // exists in a non-blocking state like Declined, in which case the server
  // will allow a new request through.
  type Resolution =
    | { kind: 'connected' }
    | { kind: 'pending-outgoing' }
    | { kind: 'pending-incoming' }
    | { kind: 'blocked' }
    | { kind: 'can-send' }

  const resolution: Resolution = (() => {
    if (!existing) return { kind: 'can-send' }
    switch (existing.userFacingStatus) {
      case UserFacingFriendStatus.Connected:
        return { kind: 'connected' }
      case UserFacingFriendStatus.Pending:
        return existing.requestedByCurrentUser
          ? { kind: 'pending-outgoing' }
          : { kind: 'pending-incoming' }
      case UserFacingFriendStatus.Blocked:
        return { kind: 'blocked' }
      default:
        return { kind: 'can-send' }
    }
  })()

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Buddy Request</DialogTitle>
      <DialogContent>
        {!isAuthenticated && (
          <Alert severity="info">
            Please sign in to send a buddy request.
          </Alert>
        )}

        {isAuthenticated && isSelf && (
          <Alert severity="info">
            That's your own profile QR — share it with someone else to be added.
          </Alert>
        )}

        {shouldFetch && loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {shouldFetch && !loading && profileError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(profileError as any)?.response?.status === 404
              ? 'That user could not be found.'
              : (profileError as any)?.response?.data?.message || (profileError as any)?.message || 'Could not load that profile.'}
          </Alert>
        )}

        {shouldFetch && !loading && profile && !sent && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
            <Avatar src={avatarSrc} alt={profile.displayName} sx={{ width: 72, height: 72 }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {profile.displayName}
              </Typography>

              {resolution.kind === 'connected' && (
                <Alert severity="success" icon={<People />} sx={{ mt: 1.5, justifyContent: 'center' }}>
                  You're already buddies with {displayName}.
                </Alert>
              )}
              {resolution.kind === 'pending-outgoing' && (
                <Alert severity="info" icon={<HourglassEmpty />} sx={{ mt: 1.5, justifyContent: 'center' }}>
                  Your buddy request to {displayName} is pending.
                </Alert>
              )}
              {resolution.kind === 'pending-incoming' && (
                <Alert severity="info" icon={<HourglassEmpty />} sx={{ mt: 1.5, justifyContent: 'center' }}>
                  {displayName} sent you a buddy request — open it from your notices to accept.
                </Alert>
              )}
              {resolution.kind === 'blocked' && (
                <Alert severity="warning" icon={<Block />} sx={{ mt: 1.5, justifyContent: 'center' }}>
                  This user is blocked. Unblock them from your settings to add them as a buddy.
                </Alert>
              )}
              {resolution.kind === 'can-send' && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Send a buddy request?
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {sendError && !sent && (
          <Alert severity="error" sx={{ mt: 1 }}>{sendError}</Alert>
        )}

        {sent && (
          <Alert severity="success" icon={<CheckCircle />}>
            Buddy request sent{profile?.displayName ? ` to ${profile.displayName}` : ''}.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {shouldFetch && !loading && profile && !sent && resolution.kind === 'can-send' && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSend}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <PersonAdd />}
          >
            Send Request
          </Button>
        )}
        <Button onClick={onClose}>
          {sent || resolution.kind !== 'can-send' ? 'Done' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
